import { ambientWorldCatalog, ambientWorldCompositionBudget } from './fishing-world-catalog'
import {
  FishingWorldSchedulerSuppressionReason,
  type FishingWorldSchedulerDecision,
  type FishingWorldSchedulerInput,
  type FishingWorldSchedulerResult,
} from './fishing-world-scheduler'

/**
 * Client-only rollout boundary for the shared scheduler. This flag is never
 * persisted: an operator must explicitly provide it to activate the new path.
 */
export type FishingWorldRolloutControl = Readonly<{
  sharedSchedulerEnabled?: boolean
}>

export const disabledFishingWorldRollout: FishingWorldRolloutControl = Object.freeze({
  sharedSchedulerEnabled: false,
})

/** Russian operator wording mirrored in the rollout runbook. */
export const fishingWorldProductionApprovalChecklist = Object.freeze([
  'Получено явное одобрение пользователя на включение выбранной production-аудитории.',
  'Одобрение зафиксировано в релизной заявке; без него включение запрещено.',
])

export type FishingWorldRolloutPath = 'ambient-fallback' | 'shared-scheduler'
export type FishingWorldSchedulerAdapter = (input: FishingWorldSchedulerInput) => FishingWorldSchedulerResult

export type FishingWorldRolloutObservation = Readonly<{
  decisionCounts: Readonly<Record<'emitted' | 'suppressed', number>>
  suppressionReasonCounts: Readonly<Partial<Record<FishingWorldSchedulerSuppressionReason, number>>>
  activeBudgetViolations: number
  clientErrors: number
}>

export function resolveFishingWorldRolloutPath(control: FishingWorldRolloutControl = disabledFishingWorldRollout): FishingWorldRolloutPath {
  return control.sharedSchedulerEnabled === true ? 'shared-scheduler' : 'ambient-fallback'
}

export function createFishingWorldRolloutObserver() {
  const observation: {
    decisionCounts: Record<'emitted' | 'suppressed', number>
    suppressionReasonCounts: Partial<Record<FishingWorldSchedulerSuppressionReason, number>>
    activeBudgetViolations: number
    clientErrors: number
  } = {
    decisionCounts: { emitted: 0, suppressed: 0 },
    suppressionReasonCounts: {},
    activeBudgetViolations: 0,
    clientErrors: 0,
  }

  const recordDecision = (result: FishingWorldSchedulerResult) => {
    const decision: FishingWorldSchedulerDecision = result.decision
    observation.decisionCounts[decision.type] += 1
    if (decision.type === 'suppressed') {
      observation.suppressionReasonCounts[decision.reason] = (observation.suppressionReasonCounts[decision.reason] ?? 0) + 1
    }

    const activeMinors = result.state.active.filter((event) => ambientWorldCatalog[event.kind].tier === 'minor')
    const minorVisualCost = activeMinors.reduce((total, event) => total + ambientWorldCatalog[event.kind].visualCost, 0)
    if (
      activeMinors.length > ambientWorldCompositionBudget.maximumActiveMinors
      || minorVisualCost > ambientWorldCompositionBudget.maximumMinorVisualCost
    ) observation.activeBudgetViolations += 1
  }

  return {
    recordDecision,
    recordClientError: () => { observation.clientErrors += 1 },
    snapshot: (): FishingWorldRolloutObservation => ({
      decisionCounts: { ...observation.decisionCounts },
      suppressionReasonCounts: { ...observation.suppressionReasonCounts },
      activeBudgetViolations: observation.activeBudgetViolations,
      clientErrors: observation.clientErrors,
    }),
  }
}

/** Runs the opt-in path only when explicitly enabled; failures fall back now. */
export function runFishingWorldRollout(
  control: FishingWorldRolloutControl,
  input: FishingWorldSchedulerInput,
  legacyAmbient: FishingWorldSchedulerAdapter,
  sharedScheduler: FishingWorldSchedulerAdapter,
  observer = createFishingWorldRolloutObserver(),
): Readonly<{ path: FishingWorldRolloutPath, result: FishingWorldSchedulerResult }> {
  if (resolveFishingWorldRolloutPath(control) === 'ambient-fallback') {
    return { path: 'ambient-fallback', result: legacyAmbient(input) }
  }

  try {
    const result = sharedScheduler(input)
    observer.recordDecision(result)
    return { path: 'shared-scheduler', result }
  } catch {
    observer.recordClientError()
    return { path: 'ambient-fallback', result: legacyAmbient(input) }
  }
}
